import { ReactNode, useRef, useEffect, useState, CSSProperties } from "react";
import { cn } from "@/utils";

type AnimationVariant = "fade-up" | "fade-left" | "fade-right" | "scale-in";

interface AnimateOnScrollProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  threshold?: number;
  triggerOnce?: boolean;
  variant?: AnimationVariant;
}

const hiddenStyles: Record<AnimationVariant, CSSProperties> = {
  "fade-up": { opacity: 0, transform: "translateY(20px)" },
  "fade-left": { opacity: 0, transform: "translateX(20px)" },
  "fade-right": { opacity: 0, transform: "translateX(-20px)" },
  "scale-in": { opacity: 0, transform: "scale(0.95)" },
};

const visibleStyles: CSSProperties = {
  opacity: 1,
  transform: "translateY(0) translateX(0) scale(1)",
};

export function AnimateOnScroll({
  children,
  className,
  delay = 0,
  threshold = 0.1,
  triggerOnce = true,
  variant = "fade-up",
}: AnimateOnScrollProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin: "0px 0px -50px 0px" }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [delay, threshold, triggerOnce, prefersReducedMotion]);

  const animStyle: CSSProperties = prefersReducedMotion
    ? {}
    : {
        ...(isVisible ? visibleStyles : hiddenStyles[variant]),
        transition: "opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
        willChange: isVisible ? "auto" : "opacity, transform",
      };

  return (
    <div
      ref={elementRef}
      className={cn(className)}
      style={animStyle}
    >
      {children}
    </div>
  );
}
